import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const KashmirGreatLakes = () => {
  const tourData = {
    name: "Kashmir Great Lakes Trek",
    duration: "06 Nights 07 Days",
    price: "₹ 14,499/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935853/wpeeo9yz50b5lkahjf5w.jpg",
    altitude: "13,000 ft",
    difficulty: "Moderate to Difficult",
    region: "Jammu & Kashmir",
    highlights: ["Alpine lakes", "Meadows", "Snow peaks"],
    bestTime: "July to September",
    description: "The Kashmir Great Lakes Trek is one of the most beautiful treks in India, taking you through pristine alpine lakes, lush meadows, and snow-capped peaks. This trek offers a perfect blend of adventure and natural beauty."
  };

  return (
    <>
      <Helmet>
        <title>Kashmir Great Lakes Trek - NomadTrailz</title>
        <meta name="description" content="Experience the stunning Kashmir Great Lakes Trek. Pristine alpine lakes, lush meadows, and snow-capped peaks await you in the beautiful Kashmir Valley." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A journey through pristine alpine lakes and lush meadows
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the heart of the Kashmir Valley, where you'll encounter 
                    crystal-clear alpine lakes, vast meadows carpeted with wildflowers, and panoramic 
                    views of the surrounding snow-capped peaks.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Multiple alpine lakes</li>
                    <li>Lush green meadows</li>
                    <li>Snow-capped peaks</li>
                    <li>Wildflower carpets</li>
                    <li>Panoramic mountain views</li>
                    <li>Kashmiri hospitality</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Arrival in Srinagar</h4>
                    <p>Arrive in Srinagar, transfer to Sonamarg and acclimatization.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Sonamarg to Nichnai</h4>
                    <p>Trek through beautiful landscapes to reach Nichnai campsite.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Nichnai to Vishansar Lake</h4>
                    <p>Cross Nichnai Pass and reach the stunning Vishansar Lake.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Vishansar to Gadsar Lake</h4>
                    <p>Visit multiple lakes including Kishansar and Gadsar Lake.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Gadsar to Satsar Lakes</h4>
                    <p>Trek to the beautiful Satsar Lakes through meadows.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Satsar to Gangabal Lake</h4>
                    <p>Cross Zajibal Pass and reach the magnificent Gangabal Lake.</p>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: Return to Srinagar</h4>
                    <p>Descend to Naranag and return to Srinagar.</p>
                  </div>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>

                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <BookingForm 
                    tourName={tourData.name}
                    tourPrice={tourData.price}
                    tourDuration={tourData.duration}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default KashmirGreatLakes;
